import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Input, 
  Button, 
  Table, 
  Space, 
  InputNumber, 
  notification,
  Modal,
  Divider,
  Typography,
  Empty,
  Switch,
  Tooltip,
  Tag
} from 'antd';
import { 
  SearchOutlined, 
  DeleteOutlined, 
  ShoppingCartOutlined,
  CheckOutlined,
  ReloadOutlined,
  SyncOutlined,
  ScanOutlined,
  BarcodeOutlined
} from '@ant-design/icons';
import { database, ref, get, update, onValue } from '../../pages/Auth/db/firebase';
import { decodeCart } from '../../utils/cartShare';
import { markListCompleted } from '../../utils/groceryListSession';
import { formatPriceWithCurrency, getFacilityCurrency, preloadCustomCurrencies } from '../../utils/currency';
import ImportListQRScanner from './ImportListQRScanner';
import BarcodeScannerModalWrapper from '../shared/BarcodeScannerModalWrapper';
import ContinuousBarcodeScanner from './ContinuousBarcodeScanner';

const { Text, Title } = Typography;

const CheckoutInterface = ({ 
  facilityId, 
  isReadOnly = false, 
  onCheckout,
  initialCart = [],
  showTitle = true 
}) => {
  const [availableItems, setAvailableItems] = useState([]);
  const [cart, setCart] = useState(initialCart);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [scanModalOpen, setScanModalOpen] = useState(false);
  const [barcodeScanModalOpen, setBarcodeScanModalOpen] = useState(false);
  const [continuousScanModalOpen, setContinuousScanModalOpen] = useState(false);
  const [importedListId, setImportedListId] = useState(null); // Track imported grocery list ID
  const [realTimeSync, setRealTimeSync] = useState(true);
  const [productInfoModal, setProductInfoModal] = useState({ open: false, product: null });
  const [facilityCurrency, setFacilityCurrency] = useState('USD');

  const [api, contextHolder] = notification.useNotification();

  // Load facility currency
  useEffect(() => {
    if (!facilityId) return;
    
    const loadCurrency = async () => {
      try {
        const currency = await getFacilityCurrency(facilityId);
        setFacilityCurrency(currency);
        // Preload custom currencies for proper formatting
        await preloadCustomCurrencies(facilityId);
      } catch (error) {
        console.error('Error loading currency:', error);
      }
    };
    
    loadCurrency();
  }, [facilityId]);

  // Load available items with real-time or manual sync
  useEffect(() => {
    if (!facilityId) return;
    
    const itemsRef = ref(database, `inventory/${facilityId}/items`);
    
    const processItemsData = (snapshot) => {
      const itemsData = [];
      if (snapshot.exists()) {
        const data = snapshot.val();
        Object.keys(data).forEach(itemName => {
          const item = data[itemName];
          if (item.quantity > 0) { // Only show items in stock
            itemsData.push({
              key: itemName,
              name: itemName,
              ...item
            });
          }
        });
      }
      setAvailableItems(itemsData);
      setLoading(false);
    };

    if (realTimeSync) {
      // Real-time listener
      setLoading(true);
      const unsubscribe = onValue(itemsRef, processItemsData, (error) => {
        api.error({ message: 'Real-time sync error', description: error.message });
        setLoading(false);
      });
      return () => unsubscribe();
    } else {
      // Manual load
      const loadItems = async () => {
        setLoading(true);
        try {
          const snapshot = await get(itemsRef);
          processItemsData(snapshot);
        } catch (error) {
          api.error({ message: 'Failed to load items', description: error.message });
          setLoading(false);
        }
      };
      loadItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId, realTimeSync]);

  const handleRefreshItems = async () => {
    if (!facilityId) return;
    setLoading(true);
    try {
      const itemsRef = ref(database, `inventory/${facilityId}/items`);
      const snapshot = await get(itemsRef);
      const itemsData = [];
      if (snapshot.exists()) {
        const data = snapshot.val();
        Object.keys(data).forEach(itemName => {
          const item = data[itemName];
          if (item.quantity > 0) {
            itemsData.push({
              key: itemName,
              name: itemName,
              ...item
            });
          }
        });
      }
      setAvailableItems(itemsData);
      api.success({ message: 'Items refreshed' });
    } catch (error) {
      api.error({ message: 'Failed to refresh items', description: error.message });
    } finally {
      setLoading(false);
    }
  };

  // Filter items based on search
  const filteredItems = availableItems.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.baseId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.barcode?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Add item to cart
  const addToCart = (item, quantity = 1) => {
    if (isReadOnly) return;
    const maxQuantity = item.quantity;
    setCart(prev => {
      const existing = prev.find(ci => ci.name === item.name);
      const currentQty = existing?.quantity || 0;
      if (currentQty + quantity > maxQuantity) {
        api.warning({
          message: 'Insufficient Stock',
          description: `Only ${maxQuantity} ${item.name} available in stock.`,
        });
        return prev;
      }
      if (existing) {
        return prev.map(ci => ci.name === item.name ? { ...ci, quantity: ci.quantity + quantity } : ci);
      }
      return [...prev, { ...item, quantity, discount: 0 }];
    });
    api.success({ message: 'Added to Cart', description: `${quantity} ${item.name} added to cart` });
  };

  // Update cart item quantity
  const updateCartQuantity = (itemName, newQuantity) => {
    if (isReadOnly) return;
    if (newQuantity <= 0) {
      removeFromCart(itemName);
      return;
    }
    const invItem = availableItems.find(i => i.name === itemName);
    if (invItem && newQuantity > invItem.quantity) {
      api.warning({ message: 'Insufficient Stock', description: `Only ${invItem.quantity} ${itemName} available.` });
      return;
    }
    setCart(prev => prev.map(ci => ci.name === itemName ? { ...ci, quantity: newQuantity } : ci));
  };

  // Update discount
  const updateDiscount = (itemName, discount) => {
    if (isReadOnly) return;
    setCart(prev => prev.map(ci =>
      ci.name === itemName ? { ...ci, discount: Math.max(0, Math.min(100, discount)) } : ci
    ));
  };

  // Remove from cart
  const removeFromCart = (itemName) => {
    if (isReadOnly) return;
    setCart(prev => prev.filter(ci => ci.name !== itemName));
  };

  // Calculate totals
  const calculateItemTotal = (item) => {
    const subtotal = item.price * item.quantity;
    const discountAmount = subtotal * (item.discount / 100);
    return subtotal - discountAmount;
  };

  // Group cart items by currency for proper display
  const cartByCurrency = cart.reduce((acc, item) => {
    const currency = item.currency || facilityCurrency;
    if (!acc[currency]) {
      acc[currency] = { items: [], total: 0, discount: 0 };
    }
    acc[currency].items.push(item);
    acc[currency].total += calculateItemTotal(item);
    acc[currency].discount += (item.price * item.quantity * (item.discount / 100));
    return acc;
  }, {});

  // Calculate totals by currency - don't filter by facility currency
  // This ensures all items are included regardless of currency
  const totalsByCurrency = cart.reduce((acc, item) => {
    const itemCurrency = item.currency || facilityCurrency;
    if (!acc[itemCurrency]) {
      acc[itemCurrency] = { total: 0, discount: 0 };
    }
    acc[itemCurrency].total += calculateItemTotal(item);
    const subtotal = item.price * item.quantity;
    acc[itemCurrency].discount += (subtotal * (item.discount / 100));
    return acc;
  }, {});

  // For backward compatibility, calculate total in facility currency
  const cartTotal = totalsByCurrency[facilityCurrency]?.total || 0;
  const totalDiscount = totalsByCurrency[facilityCurrency]?.discount || 0;

  // Handle checkout
  const handleCheckout = () => {
    console.log('HandleCheckout called, cart:', cart);
    
    if (cart.length === 0) {
      api.warning({
        message: 'Empty Cart',
        description: 'Please add items to cart before checkout.',
      });
      return;
    }

    console.log('Opening confirmation modal');
    setShowConfirmModal(true);
  };

  const handleConfirmCheckout = async () => {
    console.log('Confirmed checkout, processing...');
    setShowConfirmModal(false);
    await processCheckout();
  };

  const handleCancelCheckout = () => {
    console.log('Checkout cancelled');
    setShowConfirmModal(false);
  };

  const processCheckout = async () => {
    console.log('ProcessCheckout called');
    setCheckoutLoading(true);
    try {
      const transactionId = Date.now().toString();
      const transaction = {
        items: cart,
        total: cartTotal,
        discount: totalDiscount,
        timestamp: new Date().toISOString(),
        facilityId,
        status: 'completed',
        transactionId
      };

      console.log('Transaction data:', transaction);
      console.log('OnCheckout callback exists:', !!onCheckout);

      if (onCheckout) {
        // If parent provides checkout handler, use it
        console.log('Using parent checkout handler');
        await onCheckout(transaction);
      } else {
        // Default checkout behavior - save transaction and update inventory
        console.log('Using default checkout behavior');
        const transactionRef = ref(database, `transactions/${facilityId}/${transaction.transactionId}`);
        await update(transactionRef, transaction);

        // Update inventory quantities
        for (const item of cart) {
          const itemRef = ref(database, `inventory/${facilityId}/items/${item.name}`);
          const itemSnapshot = await get(itemRef);
          
          if (itemSnapshot.exists()) {
            const currentData = itemSnapshot.val();
            const newQuantity = Math.max(0, (currentData.quantity || 0) - item.quantity);
            await update(itemRef, { quantity: newQuantity });
          }
        }
      }

      // ========== NOTIFY CLIENT OF CHECKOUT COMPLETION ==========
      // If this was an imported grocery list, notify the client
      if (importedListId) {
        const receiptData = {
          transactionId,
          date: transaction.timestamp,
          items: cart.map(item => ({
            name: item.name,
            quantity: item.quantity,
            price: item.price
          })),
          subtotal: cartTotal + totalDiscount,
          discount: totalDiscount,
          total: cartTotal,
          facilityName: 'Store', // You can fetch this from facility data if needed
          cashierName: 'Cashier' // You can get actual cashier name if available
        };

        await markListCompleted(facilityId, importedListId, receiptData);
        setImportedListId(null); // Reset after notification
      }

      // Clear cart
      setCart([]);
      
      // Format notification with correct currency
      const notificationCurrency = Object.keys(totalsByCurrency).length === 1 
        ? Object.keys(totalsByCurrency)[0] 
        : facilityCurrency;
      const notificationTotal = totalsByCurrency[notificationCurrency]?.total || cartTotal;
      
      api.success({
        message: 'Checkout Successful',
        description: `Transaction completed. Total: ${formatPriceWithCurrency(notificationTotal, notificationCurrency, facilityId)}`,
      });
    } catch (error) {
      console.error('Checkout error:', error);
      api.error({
        message: 'Checkout Failed',
        description: error.message || 'An error occurred during checkout',
      });
    } finally {
      setCheckoutLoading(false);
    }
  };

  // Cart table columns
  const cartColumns = [
    {
      title: 'Item',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (text, record) => (
        <div style={{ wordBreak: 'break-word', whiteSpace: 'normal'}}>
          <Text style={{ color: 'var(--lcolor)' }} strong>{text}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px', color: 'var(--lcolor)' }}>
            {record.baseId}
          </Text>
          {record.barcode && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
              <BarcodeOutlined style={{ fontSize: '10px', color: 'var(--lcolor)' }} />
              <Text code strong yle={{ fontSize: '10px', color: 'var(--lcolor)' }}>{record.barcode}</Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      width: 80,
      render: (price, record) => <span style={{ whiteSpace: 'nowrap' }}>{formatPriceWithCurrency(price, record.currency || facilityCurrency, facilityId)}</span>,
    },
    {
      title: 'Qty',
      key: 'quantity',
      width: 80,
      render: (_, record) => (
        <InputNumber
          size="small"
          min={1}
          max={availableItems.find(item => item.name === record.name)?.quantity || 1}
          value={record.quantity}
          onChange={(value) => updateCartQuantity(record.name, value)}
          disabled={isReadOnly}
          style={{ width: '60px', color: 'var(--lcolor)'}}
        />
      ),
    },
    {
      title: 'Discount %',
      key: 'discount',
      width: 100,
      render: (_, record) => (
        <InputNumber
          size="small"
          min={0}
          max={100}
          value={record.discount}
          onChange={(value) => updateDiscount(record.name, value)}
          disabled={isReadOnly}
          style={{ width: '70px' }}
          formatter={value => `${value}%`}
          parser={value => value.replace('%', '')}
        />
      ),
    },
    {
      title: 'Total',
      key: 'total',
      width: 80,
      render: (_, record) => <span style={{ whiteSpace: 'nowrap' }}>{formatPriceWithCurrency(calculateItemTotal(record), record.currency || facilityCurrency, facilityId)}</span>,
    },
    ...(!isReadOnly ? [{
      title: 'Action',
      key: 'action',
      width: 60,
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeFromCart(record.name)}
          size="small"
        />
      ),
    }] : []),
  ];

  // ========== IMPORT SHARED CUSTOMER LIST FROM QR CODE ==========
  const importSharedCart = async (rawCode) => {
    if (!rawCode || !rawCode.trim()) return;
    
    // Try to extract list ID from URL (if it's a full URL)
    let extractedListId = null;
    try {
      // Check if it's a URL
      if (rawCode.includes('http://') || rawCode.includes('https://') || rawCode.includes('?')) {
        const urlMatch = rawCode.match(/[?&]listId=([A-Za-z0-9_-]+)/);
        if (urlMatch) {
          extractedListId = urlMatch[1];
          setImportedListId(extractedListId);
          console.log('Extracted list ID from URL:', extractedListId);
        }
      }
    } catch (e) {
      console.log('Not a URL, treating as direct code');
    }
    
    const { data, error } = decodeCart(rawCode.trim());
    if (error) {
      api.error({ message: 'Invalid Code', description: error });
      return;
    }
    if (data.f !== facilityId) {
      api.error({ message: 'Facility Mismatch', description: 'This list belongs to a different facility.' });
      return;
    }
    try {
      const itemsRef = ref(database, `inventory/${facilityId}/items`);
      const snapshot = await get(itemsRef);
      if (!snapshot.exists()) {
        api.error({ message: 'Inventory Empty', description: 'No items in inventory.' });
        return;
      }
      const all = snapshot.val();
      const imported = [];
      data.i.forEach(({ b, q }) => {
        const found = Object.entries(all).find(([name, itm]) => itm.baseId === b || name === b);
        if (found) {
          const [name, itm] = found;
            imported.push({
              key: name,
              name,
              baseId: itm.baseId,
              price: itm.price,
              quantity: Math.min(q, itm.quantity || q),
              discount: 0
            });
        }
      });
      if (!imported.length) {
        api.warning({ message: 'No Items Imported', description: 'Codes did not match available inventory.' });
        return;
      }
      setCart(imported);
      api.success({ message: 'List Imported', description: `${imported.length} item(s) loaded into cart.` });
    } catch (e) {
      api.error({ message: 'Import Failed', description: e.message });
    }
  };

  const handleScanDecoded = (rawCode) => {
    // Auto import after scanning
    importSharedCart(rawCode);
  };

  // Handle barcode scanning for individual items
  const handleBarcodeScanned = (barcode) => {
    // Find item by barcode (case-insensitive)
    const foundItem = availableItems.find(item => 
      item.barcode?.toLowerCase() === barcode.toLowerCase()
    );
    
    if (foundItem) {
      // Show product info modal instead of adding directly
      setProductInfoModal({ open: true, product: foundItem });
    } else {
      api.warning({
        message: 'Item Not Found',
        description: `No item found with barcode: ${barcode}`,
      });
    }
  };

  // Cart management functions for continuous scanner
  const handleAddToCart = (item, quantity = 1) => {
    console.log('handleAddToCart called with:', item, quantity);
    console.log('Current cart before add:', cart);
    addToCart(item, quantity);
  };

  const handleUpdateCart = (itemName, newQuantity) => {
    console.log('handleUpdateCart called with:', itemName, newQuantity);
    console.log('Current cart before update:', cart);
    updateCartQuantity(itemName, newQuantity);
  };

  const handleRemoveFromCart = (itemName) => {
    console.log('handleRemoveFromCart called with:', itemName);
    console.log('Current cart before remove:', cart);
    removeFromCart(itemName);
  };

  const handleContinuousCheckout = () => {
    handleCheckout();
  };

  return (
    <div className="checkout-layout" style={{ display: 'flex', gap: 'var(--spacing-lg)', height: '70vh' }}>
      {contextHolder}
      {/* Items Selection Panel */}
      <Card 
        title={showTitle ? "Available Items" : null}
        extra={!isReadOnly && (
          <Space>
            <Tooltip title={realTimeSync ? "Real-time sync ON" : "Real-time sync OFF"}>
              <Switch 
                size="small" 
                checked={realTimeSync} 
                onChange={setRealTimeSync}
                checkedChildren={<SyncOutlined />}
                unCheckedChildren={<SyncOutlined />}
              />
            </Tooltip>
            <Tooltip title="Refresh items">
              <Button size='small' icon={<ReloadOutlined />} onClick={handleRefreshItems} loading={loading} />
            </Tooltip>
            <Button size='small' onClick={() => setScanModalOpen(true)}>Scan Customer List</Button>
            <Button 
              size='small' 
              type="primary"
              icon={<ScanOutlined />}
              onClick={() => setContinuousScanModalOpen(true)}
              style={{ background: 'var(--success-color)', borderColor: 'var(--success-color)' }}
            >
              Scan Cart
            </Button>
          </Space>
        )}
        style={{ flex: 1, overflow: 'hidden', boxShadow: 'var(--shadow-item)' }}
        bodyStyle={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      >
        <Input
          placeholder="Search items by name, category, or barcode..."
          prefix={<SearchOutlined />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          suffix={
            searchTerm ? (
              <Button
                type="text"
                size="small"
                onClick={() => setSearchTerm('')}
                style={{ color: 'var(--text-tertiary)' }}
              >
                ×
              </Button>
            ) : (
              <Button 
                type="text" 
                size="small"
                icon={<ScanOutlined />}
                onClick={() => setBarcodeScanModalOpen(true)}
                title="Scan barcode to add item"
                style={{ color: 'var(--primary-color)' }}
              />
            )
          }
          size="large"
          style={{ marginBottom: 'var(--spacing-md)' }}
        />
        
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
          {filteredItems.length === 0 ? (
            <Empty description="No items found" />
          ) : (
            <div className="product-grid">
              {filteredItems.map((item) => (
                <div
                  key={item.key}
                  className="product-card"
                  draggable={!isReadOnly}
                  onDragStart={(e) => {
                    if (isReadOnly) return;
                    e.dataTransfer.setData('application/json', JSON.stringify({
                      name: item.name,
                      baseId: item.baseId,
                      price: item.price,
                      currency: item.currency || facilityCurrency,
                      category: item.category,
                      barcode: item.barcode,
                      purchasingPrice: item.purchasingPrice
                    }));
                    e.dataTransfer.effectAllowed = 'copy';
                    e.currentTarget.style.opacity = '0.5';
                    e.currentTarget.setAttribute('data-dragging', 'true');
                  }}
                  onDragEnd={(e) => {
                    e.currentTarget.style.opacity = '1';
                    e.currentTarget.removeAttribute('data-dragging');
                  }}
                  onTouchStart={(e) => {
                    if (isReadOnly) return;
                    const touch = e.touches[0];
                    const card = e.currentTarget;
                    card.setAttribute('data-touch-start', JSON.stringify({
                      x: touch.clientX,
                      y: touch.clientY,
                      time: Date.now()
                    }));
                    card.style.opacity = '0.7';
                  }}
                  onTouchMove={(e) => {
                    if (isReadOnly) return;
                    e.preventDefault();
                    const card = e.currentTarget;
                    const startData = card.getAttribute('data-touch-start');
                    if (startData) {
                      const start = JSON.parse(startData);
                      const touch = e.touches[0];
                      const deltaX = Math.abs(touch.clientX - start.x);
                      const deltaY = Math.abs(touch.clientY - start.y);
                      if (deltaX > 10 || deltaY > 10) {
                        card.setAttribute('data-dragging', 'true');
                        card.style.opacity = '0.5';
                      }
                    }
                  }}
                  onTouchEnd={(e) => {
                    if (isReadOnly) return;
                    const card = e.currentTarget;
                    const startData = card.getAttribute('data-touch-start');
                    if (startData) {
                      const start = JSON.parse(startData);
                      const touch = e.changedTouches[0];
                      const deltaX = Math.abs(touch.clientX - start.x);
                      const deltaY = Math.abs(touch.clientY - start.y);
                      const timeDelta = Date.now() - start.time;
                      
                      // Check if it was a drag (moved more than 20px)
                      if ((deltaX > 20 || deltaY > 20) && timeDelta < 1000) {
                        // Try to find drop zone using elementFromPoint
                        let dropZone = null;
                        try {
                          dropZone = document.elementFromPoint(touch.clientX, touch.clientY);
                        } catch (err) {
                          console.error('Error getting element from point:', err);
                        }
                        
                        // Also check if cart panel is visible (alternative method)
                        const cartPanel = document.querySelector('[data-cart-drop-zone]');
                        const wasDragging = card.hasAttribute('data-dragging');
                        
                        // If we were dragging and cart is visible, add the item
                        if (wasDragging && cartPanel) {
                          const existingItem = cart.find(i => i.name === item.name);
                          if (existingItem) {
                            updateCartQuantity(item.name, existingItem.quantity + 1);
                            api.success({ message: 'Item Updated', description: `Added 1 more ${item.name}` });
                          } else {
                            addToCart(item, 1);
                            api.success({ message: 'Item Added', description: `${item.name} added to cart` });
                          }
                        } else if (dropZone) {
                          // Try the original method
                          let target = dropZone;
                          while (target && target !== document.body) {
                            if (target.hasAttribute('data-cart-drop-zone')) {
                              const existingItem = cart.find(i => i.name === item.name);
                              if (existingItem) {
                                updateCartQuantity(item.name, existingItem.quantity + 1);
                                api.success({ message: 'Item Updated', description: `Added 1 more ${item.name}` });
                              } else {
                                addToCart(item, 1);
                                api.success({ message: 'Item Added', description: `${item.name} added to cart` });
                              }
                              break;
                            }
                            target = target.parentElement;
                          }
                        }
                      }
                    }
                    card.style.opacity = '1';
                    card.removeAttribute('data-touch-start');
                    card.removeAttribute('data-dragging');
                  }}
                  onClick={() => !isReadOnly && setProductInfoModal({ open: true, product: item })}
                  style={{ 
                    cursor: isReadOnly ? 'default' : 'grab',
                    opacity: isReadOnly ? 0.7 : 1,
                    boxShadow: 'var(--shadow-card)',
                    touchAction: 'none'
                  }}
                >
                  <div className="product-card-title">{item.name}</div>
                  <div className="product-card-meta">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Tag color="blue" style={{ margin: 0, fontSize: '11px' }}>{item.category}</Tag>
                    </div>
                    {item.barcode && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px' }}>
                        <BarcodeOutlined style={{ fontSize: '12px', color: 'var(--lcolor)' }} />
                        <Text code strong style={{ fontSize: '11px', color: 'var(--lcolor)' }}>{item.barcode}</Text>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto' }}>
                    <div className="product-card-price">
                      {formatPriceWithCurrency(item.price, item.currency, facilityId)}
                    </div>
                    <Tag color={item.quantity > 10 ? 'green' : item.quantity > 0 ? 'orange' : 'red'} style={{ margin: 0 }}>
                      {item.quantity} left
                    </Tag>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Shopping Cart Panel */}
      <Card
        title={
          <Space>
            <ShoppingCartOutlined />
            Shopping Cart ({cart.reduce((s,i)=>s+(i.quantity||0),0)})
          </Space>
        }
        style={{ flex: 1, }}
        extra={
          !isReadOnly && (
            <Button
              type="primary"
              icon={<CheckOutlined />}
              onClick={handleCheckout}
              disabled={cart.length === 0}
              loading={checkoutLoading}
            >
              Checkout
            </Button>
          )
        }
      >
        <div
          data-cart-drop-zone="true"
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = 'copy';
            e.currentTarget.style.backgroundColor = 'var(--surface-tertiary)';
            e.currentTarget.style.border = '2px dashed var(--primary-color)';
          }}
          onDragLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.border = 'none';
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.border = 'none';
            const itemData = e.dataTransfer.getData('application/json');
            if (itemData) {
              try {
                const item = JSON.parse(itemData);
                const existingItem = cart.find(i => i.name === item.name);
                if (existingItem) {
                  updateCartQuantity(item.name, existingItem.quantity + 1);
                  api.success({ message: 'Item Updated', description: `Added 1 more ${item.name}` });
                } else {
                  addToCart(item, 1);
                }
              } catch (err) {
                console.error('Error parsing dropped item:', err);
              }
            }
          }}
          style={{ minHeight: '200px' }}
        >
        {cart.length === 0 ? (
          <Empty description="Cart is empty - Drag items here to add them" />
        ) : (
          <>
            <div style={{ overflowX: 'auto', color: 'var(--lcolor)' }}>
              <Table
                columns={cartColumns}
                dataSource={cart}
                pagination={false}
                size="small"
                scroll={{ y: 300, x: 'max-content' }}
                style={{ minWidth: '600px' }}
              />
            </div>
            
            <Divider />
            
            <div style={{ textAlign: 'right' }}>
              {Object.keys(cartByCurrency).length > 1 ? (
                // Show totals by currency when mixed currencies
                <div>
                  {Object.entries(cartByCurrency).map(([currency, data]) => (
                    <div key={currency} style={{ marginBottom: '8px' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {currency} Items:
                      </Text>
                      <br />
                      {data.discount > 0 && (
                        <>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            Subtotal: {formatPriceWithCurrency(data.total + data.discount, currency, facilityId)}
                            <br />
                            Discount: -{formatPriceWithCurrency(data.discount, currency, facilityId)}
                            <br />
                          </Text>
                        </>
                      )}
                      <Text strong style={{ color: 'var(--primary-color)' }}>
                        {formatPriceWithCurrency(data.total, currency, facilityId)}
                      </Text>
                    </div>
                  ))}
                </div>
              ) : (
                // Show single currency total - use the actual currency of items, not facility currency
                <div>
                  {(() => {
                    const singleCurrency = Object.keys(cartByCurrency)[0] || facilityCurrency;
                    const singleTotal = cartByCurrency[singleCurrency]?.total || 0;
                    const singleDiscount = cartByCurrency[singleCurrency]?.discount || 0;
                    return (
                      <>
                        {singleDiscount > 0 && (
                          <Text type="secondary">
                            Subtotal: {formatPriceWithCurrency(singleTotal + singleDiscount, singleCurrency, facilityId)}
                            <br />
                            Discount: -{formatPriceWithCurrency(singleDiscount, singleCurrency, facilityId)}
                            <br />
                          </Text>
                        )}
                        <Title level={4} style={{ margin: 0, color: 'var(--primary-color)' }}>
                          Total: {formatPriceWithCurrency(singleTotal, singleCurrency, facilityId)}
                        </Title>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          </>
        )}
        </div>
      </Card>

      {/* Checkout Confirmation Modal */}
      <Modal
  title="Confirm Checkout"
        open={showConfirmModal}
        onOk={handleConfirmCheckout}
        onCancel={handleCancelCheckout}
        okText="Confirm Checkout"
        cancelText="Cancel"
        confirmLoading={checkoutLoading}
        okButtonProps={{ 
          type: 'primary',
          danger: false
        }}
      >
        <div>
          <p>Process checkout for {cart.reduce((s,i)=>s+(i.quantity||0),0)} item(s)?</p>
          {Object.keys(cartByCurrency).length > 1 ? (
            <div>
              <p><strong>Totals by Currency:</strong></p>
              {Object.entries(cartByCurrency).map(([currency, data]) => (
                <p key={currency} style={{ margin: '4px 0' }}>
                  <strong>{currency}: {formatPriceWithCurrency(data.total, currency)}</strong>
                  {data.discount > 0 && (
                    <span style={{ color: 'var(--success-color)', fontSize: '12px' }}>
                      {' '}(discount: -{formatPriceWithCurrency(data.discount, currency)})
                    </span>
                  )}
                </p>
              ))}
            </div>
          ) : (
            <div>
              {(() => {
                const singleCurrency = Object.keys(cartByCurrency)[0] || facilityCurrency;
                const singleTotal = cartByCurrency[singleCurrency]?.total || 0;
                const singleDiscount = cartByCurrency[singleCurrency]?.discount || 0;
                return (
                  <>
                    <p><strong>Total: {formatPriceWithCurrency(singleTotal, singleCurrency, facilityId)}</strong></p>
                    {singleDiscount > 0 && (
                      <p style={{ color: 'var(--success-color)' }}>
                        Total discounts: {formatPriceWithCurrency(singleDiscount, singleCurrency, facilityId)}
                      </p>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </div>
      </Modal>

      {/* ========== QR IMPORT GROCERY LIST FEATURE ========== */}
      {/* QR Code Scanner Modal for importing customer grocery list */}
      <ImportListQRScanner open={scanModalOpen} onClose={() => setScanModalOpen(false)} onDecoded={handleScanDecoded} />
      
      {/* Barcode Scanner Modal */}
      <BarcodeScannerModalWrapper
        visible={barcodeScanModalOpen}
        onClose={() => setBarcodeScanModalOpen(false)}
        onScan={handleBarcodeScanned}
        title="Scan Product Barcode"
        allowManualInput={true}
        autoClose={true}
        showResult={false}
        size="medium"
      />

      {/* Continuous Barcode Scanner Modal */}
      <ContinuousBarcodeScanner
        visible={continuousScanModalOpen}
        onClose={() => setContinuousScanModalOpen(false)}
        onCheckout={handleContinuousCheckout}
        availableItems={availableItems}
        cart={cart}
        onAddToCart={handleAddToCart}
        onUpdateCart={handleUpdateCart}
        onRemoveFromCart={handleRemoveFromCart}
        facilityId={facilityId}
      />

      {/* Product Info Modal */}
      <Modal
        title="Product Information"
        open={productInfoModal.open}
        onCancel={() => setProductInfoModal({ open: false, product: null })}
        footer={[
          <Button 
            key="cancel" 
            onClick={() => setProductInfoModal({ open: false, product: null })}
          >
            Cancel
          </Button>,
          <Button
            key="add"
            type="primary"
            icon={<ShoppingCartOutlined />}
            onClick={() => {
              if (productInfoModal.product) {
                addToCart(productInfoModal.product, 1);
                setProductInfoModal({ open: false, product: null });
              }
            }}
          >
            Add to Cart
          </Button>
        ]}
      >
        {productInfoModal.product && (
          <Space direction="vertical" size="middle" style={{ width: '100%'}}>
            <div>
              <Text strong style={{ fontSize: '18px', color: 'var(--lcolor)' }}>{productInfoModal.product.name}</Text>
            </div>
            <div>
              <Tag color="blue">{productInfoModal.product.category}</Tag>
            </div>
            {productInfoModal.product.barcode && (
              <div>
                <Text strong style={{ color: 'var(--lcolor)' }} type="secondary">Barcode: </Text>
                <Text code strong style={{ color: 'var(--lcolor)' }}>{productInfoModal.product.barcode}</Text>
              </div>
            )}
            <div>
              <Text strong style={{ color: 'var(--lcolor)' }}>Price: </Text>
              <Text strong style={{ fontSize: '16px', color: 'var(--primary-color)' }}>
                {formatPriceWithCurrency(productInfoModal.product.price, productInfoModal.product.currency || facilityCurrency, facilityId)}
              </Text>
            </div>
            <div>
              <Text strong style={{ color: 'var(--lcolor)' }} type="secondary">Stock: </Text>
              <Tag color={productInfoModal.product.quantity > 10 ? 'green' : productInfoModal.product.quantity > 0 ? 'orange' : 'red'}>
                {productInfoModal.product.quantity} available
              </Tag>
            </div>
          </Space>
        )}
      </Modal>
    </div>
  );
};

export default CheckoutInterface;